import {
  type CrusherS3Plan,
  crusherS3PlanSchema,
  inspectPlanFileApi,
  PlanFileReadError
} from 'api-validations/crusher'
import { PlanAsset } from 'api-validations/locutus'
import { MeasureSystem } from 'api-validations/shared'
import { useApiClient } from 'shared-ui/api-provider'
import { ApiError } from 'utils'
import type { z } from 'zod'

import type { ReadableRequest } from '../lib/planUpload/inspectRequest'
import { type ParsedPlanEntry, rateColumns, withRateColumn } from '../lib/planUpload/planEntry'
import { assetIdFor, entryId, type PlanUploadSource } from '../lib/planUpload/planUploadReducer'

type ConvertedFile = z.infer<typeof inspectPlanFileApi.responseSchema>['files'][number]

type PrescriptionEntry = Extract<ParsedPlanEntry, { assetType: PlanAsset.Prescription }>

const toEntries = ({
  plan,
  file,
  source,
  index
}: {
  plan: CrusherS3Plan
  file: ConvertedFile
  source: PlanUploadSource
  index: number
}): readonly ParsedPlanEntry[] => {
  const assetId = assetIdFor({ sourceId: source.id, index })
  const found = {
    id: assetId,
    sourceId: source.id,
    assetId,
    // Crusher can return a shapefile's plan unnamed.
    sourceName: file.name === '' ? source.file.name : file.name,
    cff: file.cff ?? undefined,
    // Crusher reports a CFF only for .2020 assets, which also state their own asset type.
    assetTypeIsSettled: file.cff !== null
  }

  if (plan.type === 'boundary')
    return [
      {
        ...found,
        assetType: PlanAsset.Boundary,
        boundary: plan.data.boundary,
        inverted: plan.data.inverted
      }
    ]

  const prescription: PrescriptionEntry = {
    ...found,
    assetType: PlanAsset.Prescription,
    prescription: plan.data.prescription,
    applicationType: plan.data.type
  }

  const columns = rateColumns(plan.data.prescription)
  if (found.assetTypeIsSettled || columns.length < 2) return [prescription]

  return columns.map((column) => ({
    ...prescription,
    id: entryId({ assetId, rateColumn: column }),
    rateColumn: column,
    prescription: withRateColumn({ prescription: plan.data.prescription, column })
  }))
}

/**
 * A converted asset that could not be fetched or parsed. User-unfriendly, so
 * it is logged rather than shown.
 */
class ConvertedPlanError extends Error {}

const readConvertedPlan = async (url: string): Promise<CrusherS3Plan> => {
  try {
    const response = await fetch(url)
    if (!response.ok) throw new ApiError(response, await response.text())
    return crusherS3PlanSchema.parse(await response.json())
  } catch (cause) {
    throw new ConvertedPlanError('Could not read a converted asset', { cause })
  }
}

/**
 * The assets that could be read, and how many could not.
 */
export type PlanFileReadResult = {
  entries: readonly ParsedPlanEntry[]
  unreadable: number
}

/**
 * Reads one source into its boundaries and prescriptions without storing anything. An unreadable
 * asset is skipped and counted; the read fails only when none can be read, with a
 * `PlanReadProblem`.
 */
export const useReadPlanFile = () => {
  const apiClient = useApiClient()

  return async ({
    source,
    request
  }: {
    source: PlanUploadSource
    request: ReadableRequest
  }): Promise<PlanFileReadResult> => {
    // Crusher refuses a .2020 that holds no plans as an empty file, not a failure.
    const files = await apiClient
      .callApi(
        inspectPlanFileApi,
        {
          from: request.from,
          type:
            source.reading.assetType === PlanAsset.Prescription
              ? source.reading.applicationType
              : 'boundary',
          measureSystem:
            source.reading.assetType === PlanAsset.Prescription
              ? source.reading.measureSystem
              : MeasureSystem.Imperial
        },
        request.files
      )
      .then(({ files: converted }) => converted)
      .catch((error: PlanFileReadError) => {
        if (error === PlanFileReadError.NoAssets) return []
        throw error
      })

    const read = await Promise.allSettled(
      files.map(async (file, index) =>
        toEntries({ plan: await readConvertedPlan(file.url), file, source, index })
      )
    )

    const entries = read.flatMap((result) => (result.status === 'fulfilled' ? result.value : []))

    const failures = read.filter(
      (result): result is PromiseRejectedResult => result.status === 'rejected'
    )
    for (const { reason } of failures) console.warn(reason)

    if (files.length > 0 && failures.length === files.length) {
      throw PlanFileReadError.Unknown
    }

    return { entries, unreadable: failures.length }
  }
}
